
export interface Utterance {
  text: string;
  confidence: number;
  atMs: number;
}

export type DetectorStatus = 'idle' | 'starting' | 'ready' | 'listening' | 'error';

export interface Detector {
  readonly status: DetectorStatus;
  load(): Promise<void>;
  start(stream: MediaStream): Promise<void>;
  stop(): void;
  onUtterance(handler: (utterance: Utterance) => void): () => void;
  onError(handler: (message: string, fatal: boolean) => void): () => void;
  onStatusChange(handler: (status: DetectorStatus) => void): () => void;
  dispose(): void;
}


interface SpeechRecognitionAlternativeLike {
  transcript: string;
  confidence: number;
}
interface SpeechRecognitionResultLike {
  readonly length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}
interface SpeechRecognitionEventLike {
  results: SpeechRecognitionResultListLike;
}
interface SpeechRecognitionErrorEventLike {
  error: string;
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function speechRecognitionCtor(): SpeechRecognitionCtor | null {
  const w = globalThis as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const START_WATCHDOG_MS = 2500;

const UNSUPPORTED_MESSAGE =
  'This browser cannot listen for spoken words. Battlequip needs Chrome or Safari — ' +
  'Firefox has the speech API but no recognition service behind it.';

export class WebSpeechDetector implements Detector {
  #status: DetectorStatus = 'idle';
  #recognition: SpeechRecognitionLike | null = null;
  #running = false;
  #started = false;
  #everStarted = false;
  #watchdog: ReturnType<typeof setTimeout> | null = null;
  #emitted = 0;
  #utteranceHandlers = new Set<(utterance: Utterance) => void>();
  #errorHandlers = new Set<(message: string, fatal: boolean) => void>();
  #statusHandlers = new Set<(status: DetectorStatus) => void>();

  get status(): DetectorStatus {
    return this.#status;
  }

  load(): Promise<void> {
    if (!speechRecognitionCtor()) {
      this.#setStatus('error');
      this.#report(UNSUPPORTED_MESSAGE, true);
      return Promise.reject(new Error(UNSUPPORTED_MESSAGE));
    }
    this.#setStatus('ready');
    return Promise.resolve();
  }

  start(stream: MediaStream): Promise<void> {
    void stream;
    const Ctor = speechRecognitionCtor();
    if (!Ctor) return Promise.reject(new Error(UNSUPPORTED_MESSAGE));
    if (this.#running) return Promise.resolve();

    this.#running = true;
    this.#setStatus('starting');

    return new Promise<void>((resolve, reject) => {
      const fail = (message: string): void => {
        this.#clearWatchdog();
        this.#running = false;
        this.#setStatus('error');
        this.#report(message, true);
        reject(new Error(message));
      };

      this.#watchdog = setTimeout(() => {
        if (this.#started) return;
        try {
          this.#recognition?.abort();
        } catch {
        }
        fail(UNSUPPORTED_MESSAGE);
      }, START_WATCHDOG_MS);

      this.#open(Ctor, {
        onStarted: () => {
          this.#setStatus('listening');
          resolve();
        },
        onFailed: fail,
      });
    });
  }

  #open(
    Ctor: SpeechRecognitionCtor,
    hooks: { onStarted: () => void; onFailed: (message: string) => void },
  ): void {
    const rec = new Ctor();
    this.#recognition = rec;
    this.#started = false;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      this.#started = true;
      this.#everStarted = true;
      this.#clearWatchdog();
      hooks.onStarted();
    };

    rec.onresult = (event) => this.#onResult(event);

    rec.onend = () => {
      if (!this.#running) return;
      this.#emitted = 0;
      this.#open(Ctor, hooks);
    };

    rec.onerror = (event) => {
      if (this.#everStarted) {
        if (event.error === 'no-speech' || event.error === 'aborted') return;
        this.#report(`speech recognition error: ${event.error}`, false);
        return;
      }
      hooks.onFailed(startupErrorMessage(event.error));
    };

    try {
      rec.start();
    } catch (err) {
      if (this.#everStarted) {
        this.#report(err instanceof Error ? err.message : 'recognition restart failed', false);
        return;
      }
      hooks.onFailed(
        `Could not start speech recognition: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  #onResult(event: SpeechRecognitionEventLike): void {
    const atMs = Date.now();
    for (let i = this.#emitted; i < event.results.length; i++) {
      const result = event.results[i];
      if (!result || !result.isFinal) break;
      this.#emitted = i + 1;
      const alt = result.length > 0 ? result[0] : undefined;
      const text = alt?.transcript?.trim();
      if (!text) continue;
      const raw = alt?.confidence;
      const confidence = typeof raw === 'number' && raw > 0 && raw <= 1 ? raw : 1;
      for (const handler of this.#utteranceHandlers) {
        handler({ text, confidence, atMs });
      }
    }
  }

  stop(): void {
    this.#running = false;
    this.#clearWatchdog();
    const rec = this.#recognition;
    this.#recognition = null;
    if (rec) {
      rec.onend = null;
      rec.onresult = null;
      rec.onerror = null;
      try {
        rec.stop();
      } catch {
      }
    }
    this.#emitted = 0;
    if (this.#status === 'listening' || this.#status === 'starting') this.#setStatus('ready');
  }

  onUtterance(handler: (utterance: Utterance) => void): () => void {
    this.#utteranceHandlers.add(handler);
    return () => this.#utteranceHandlers.delete(handler);
  }

  onError(handler: (message: string, fatal: boolean) => void): () => void {
    this.#errorHandlers.add(handler);
    return () => this.#errorHandlers.delete(handler);
  }

  onStatusChange(handler: (status: DetectorStatus) => void): () => void {
    this.#statusHandlers.add(handler);
    return () => this.#statusHandlers.delete(handler);
  }

  dispose(): void {
    this.stop();
    this.#emitted = 0;
    this.#utteranceHandlers.clear();
    this.#errorHandlers.clear();
    this.#statusHandlers.clear();
    this.#status = 'idle';
  }

  #clearWatchdog(): void {
    if (this.#watchdog !== null) clearTimeout(this.#watchdog);
    this.#watchdog = null;
  }

  #setStatus(status: DetectorStatus): void {
    this.#status = status;
    for (const handler of this.#statusHandlers) handler(status);
  }

  #report(message: string, fatal: boolean): void {
    for (const handler of this.#errorHandlers) handler(message, fatal);
  }
}

function startupErrorMessage(code: string): string {
  switch (code) {
    case 'not-allowed':
      return 'Microphone access was denied, so the game cannot hear your words.';
    case 'service-not-allowed':
      return UNSUPPORTED_MESSAGE;
    case 'language-not-supported':
      return 'This browser has no English speech recognition available.';
    case 'audio-capture':
      return 'No microphone was found.';
    default:
      return `Speech recognition could not start (${code}).`;
  }
}
